import { DateField, TextField } from "@louise/moon-ui";
import * as Clipboard from "expo-clipboard";
import { useEffect, useRef, useState } from "react";
import {
  Pressable,
  ScrollView,
  Text,
  View,
  useWindowDimensions,
} from "react-native";
import {
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  Check,
  ClipboardCopy,
  DollarSign,
  House,
  Mail,
  MapPin,
  Phone,
  Stethoscope,
  UserRound,
  UsersRound,
  WalletCards,
  type LucideIcon,
} from "lucide-react-native";
import {
  ActionBanner,
  Dialog,
  DialogActions,
  DialogHeader,
  DialogIcon,
  PrimaryButton,
  AddressField,
  type AddressData,
  SecondaryButton,
  haptic,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import {
  usePatient,
  useUpdatePatient,
} from "@src/packages/patients/hooks/usePatient";
import type { UpdatePatientInput } from "@src/packages/patients/update-patient-request";
import type { Patient } from "@louise/medical-history/basic-info/calculateBasicInfoCompletion";
import { EMBER } from "../../../onboarding/theme";
import { useOnboarding } from "../../../onboarding/OnboardingContext";
import type { PrefillData } from "./useApplicationPrefill";
import { semanticColors } from "@louise/theme/tokens";

const APPLICANT_DIALOG_ILLUSTRATION =
  require("../../../assets/images/document-upload-illustration.png") as number;

export type ApplicantSnapshot = {
  fullName: string;
  dateOfBirth: string;
  email: string;
  phone: string;
  addressLine1: string;
  city: string;
  country: string;
  state: string;
  zipCode: string;
  clinicName: string;
  treatmentType: string;
  estimatedTreatmentCost: string;
  requestedFinancingAmount: string;
  employmentStatus: string;
  employer: string;
  occupation: string;
  annualGrossIncome: string;
  householdAnnualIncome: string;
  housingStatus: string;
  monthlyHousingCost: string;
  monthlyDebtPayments: string;
  coApplicantFullName: string;
  coApplicantDateOfBirth: string;
  coApplicantEmail: string;
  coApplicantPhone: string;
  coApplicantAnnualGrossIncome: string;
  coApplicantEmploymentStatus: string;
  coApplicantRelationship: string;
};

function snapshotFromPrefill(prefill: PrefillData): ApplicantSnapshot {
  return {
    fullName: prefill.fullName ?? "",
    dateOfBirth: prefill.dateOfBirth ?? "",
    email: prefill.email ?? "",
    phone: prefill.phone ?? "",
    addressLine1: prefill.addressLine1 ?? "",
    city: prefill.city ?? "",
    country: prefill.country ?? "",
    state: prefill.state ?? "",
    zipCode: prefill.zipCode ?? "",
    clinicName: "",
    treatmentType: prefill.treatmentType ?? "",
    estimatedTreatmentCost: "",
    requestedFinancingAmount: "",
    employmentStatus: "",
    employer: prefill.employer ?? "",
    occupation: "",
    annualGrossIncome: "",
    householdAnnualIncome: "",
    housingStatus: "",
    monthlyHousingCost: "",
    monthlyDebtPayments: "",
    coApplicantFullName: "",
    coApplicantDateOfBirth: "",
    coApplicantEmail: "",
    coApplicantPhone: "",
    coApplicantAnnualGrossIncome: "",
    coApplicantEmploymentStatus: "",
    coApplicantRelationship: "",
  };
}

type Props = {
  visible: boolean;
  prefill: PrefillData;
  initialValue: ApplicantSnapshot | null;
  onClose: () => void;
  onSave: (snapshot: ApplicantSnapshot) => void;
};

function SnapshotField({
  label,
  value,
  onChangeText,
  placeholder,
  hint,
  keyboardType,
  labelIcon: LabelIcon,
}: {
  label: string;
  value: string;
  onChangeText: (value: string) => void;
  placeholder?: string;
  hint?: string;
  keyboardType?: "default" | "email-address" | "numeric" | "phone-pad";
  labelIcon: LucideIcon;
}) {
  return (
    <TextField
      label={label}
      labelIcon={LabelIcon}
      hint={hint}
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      keyboardType={keyboardType}
      autoCapitalize="none"
    />
  );
}

type PatientTreatment = NonNullable<Patient["treatments"]>[number];
type PatientTreatmentIntent = NonNullable<PatientTreatment["intent"]>;

const PATIENT_TREATMENT_INTENTS = [
  "Exploring",
  "Preservation",
  "Donation",
  "Conception",
  "StartingTreatment",
  "InTreatment",
] as const satisfies readonly PatientTreatmentIntent[];

function nullableValue(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed ? trimmed : null;
}

function splitFullName(value: string): {
  firstName: string | null;
  lastName: string | null;
} {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] ?? null,
    lastName: parts.slice(1).join(" ") || null,
  };
}

function normalizeDateOfBirth(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

  const parts = trimmed.split(/[/-]/).map((part) => Number(part));
  if (parts.length !== 3 || parts.some((part) => !Number.isInteger(part))) {
    return null;
  }

  const [month, day, year] = parts;
  if (
    month == null ||
    day == null ||
    year == null ||
    year < 1900 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return null;
  }

  return `${year.toString().padStart(4, "0")}-${month
    .toString()
    .padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
}

function getTreatmentIntent(value: string): PatientTreatmentIntent | null {
  const normalized = value.trim().toLowerCase();
  return (
    PATIENT_TREATMENT_INTENTS.find(
      (intent) => intent.toLowerCase() === normalized,
    ) ?? null
  );
}

export function formatSnapshotForClipboard(
  snapshot: ApplicantSnapshot,
): string {
  const line = (label: string, value: string): string | null => {
    const trimmed = value.trim();
    return trimmed ? `${label}: ${trimmed}` : null;
  };
  const addressParts = [
    snapshot.addressLine1,
    snapshot.city,
    snapshot.state,
    snapshot.zipCode,
  ]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(", ");
  const coApplicantLines = [
    line("Co-applicant name", snapshot.coApplicantFullName),
    line("Co-applicant date of birth", snapshot.coApplicantDateOfBirth),
    line("Co-applicant email", snapshot.coApplicantEmail),
    line("Co-applicant phone", snapshot.coApplicantPhone),
    line(
      "Co-applicant annual gross income",
      snapshot.coApplicantAnnualGrossIncome,
    ),
    line(
      "Co-applicant employment status",
      snapshot.coApplicantEmploymentStatus,
    ),
    line("Co-applicant relationship", snapshot.coApplicantRelationship),
  ].filter((value): value is string => value != null);
  const sections = [
    [
      "Applicant",
      line("Full name", snapshot.fullName),
      line("Date of birth", snapshot.dateOfBirth),
      line("Email", snapshot.email),
      line("Phone", snapshot.phone),
      addressParts ? `Address: ${addressParts}` : null,
    ],
    [
      "Treatment",
      line("Clinic", snapshot.clinicName),
      line("Treatment type", snapshot.treatmentType),
      line("Estimated treatment cost", snapshot.estimatedTreatmentCost),
      line("Requested financing amount", snapshot.requestedFinancingAmount),
    ],
    [
      "Employment & income",
      line("Employment status", snapshot.employmentStatus),
      line("Employer", snapshot.employer),
      line("Occupation", snapshot.occupation),
      line("Annual gross income", snapshot.annualGrossIncome),
      line("Household annual income", snapshot.householdAnnualIncome),
    ],
    [
      "Housing & debt",
      line("Housing status", snapshot.housingStatus),
      line("Monthly housing cost", snapshot.monthlyHousingCost),
      line("Monthly debt payments", snapshot.monthlyDebtPayments),
    ],
    coApplicantLines.length > 0 ? ["Co-applicant", ...coApplicantLines] : null,
  ].filter((section): section is string[] => section != null);

  return sections
    .map(([heading, ...lines]) => {
      const body = lines.filter(
        (value): value is string => typeof value === "string",
      );
      if (body.length === 0) return null;
      return `${heading}\n${body.join("\n")}`;
    })
    .filter((block): block is string => block != null)
    .join("\n\n");
}

function buildPatientUpdate(
  snapshot: ApplicantSnapshot,
  patient: Patient | undefined,
): UpdatePatientInput {
  const { firstName, lastName } = splitFullName(snapshot.fullName);
  const email = nullableValue(snapshot.email);
  const phoneNumber = nullableValue(snapshot.phone);
  const dateOfBirth = normalizeDateOfBirth(snapshot.dateOfBirth);
  const existingAddress = patient?.addresses?.[0];
  const addressLine1 = nullableValue(snapshot.addressLine1);
  const city = nullableValue(snapshot.city);
  const country = nullableValue(snapshot.country);
  const postalCode = nullableValue(snapshot.zipCode);
  const treatmentIntent = getTreatmentIntent(snapshot.treatmentType);
  const existingTreatment = patient?.treatments?.[0];
  const update: UpdatePatientInput = {};

  if (firstName !== (patient?.firstName ?? null)) update.firstName = firstName;
  if (lastName !== (patient?.lastName ?? null)) update.lastName = lastName;
  if (email !== (patient?.email ?? null)) update.email = email ?? "";
  if (phoneNumber !== (patient?.phoneNumber ?? null)) {
    update.phoneNumber = phoneNumber;
  }
  if (dateOfBirth && dateOfBirth !== (patient?.dateOfBirth ?? null)) {
    update.dateOfBirth = dateOfBirth;
  }

  const addressChanged =
    addressLine1 !== (existingAddress?.address1 ?? null) ||
    city !== (existingAddress?.city ?? null) ||
    postalCode !== (existingAddress?.postalCode ?? null) ||
    country !== (existingAddress?.country ?? null);

  if (addressChanged) {
    update.addresses = [
      {
        ...(existingAddress?.id ? { id: existingAddress.id } : {}),
        address1: addressLine1,
        address2: existingAddress?.address2 ?? null,
        city,
        postalCode,
        country,
      },
    ];
  }

  if (treatmentIntent && treatmentIntent !== existingTreatment?.intent) {
    update.treatments = [
      {
        ...(existingTreatment?.id ? { id: existingTreatment.id } : {}),
        intent: treatmentIntent,
        date: existingTreatment?.date ?? null,
      },
    ];
  }

  return update;
}

export default function ApplicantInfoDialog({
  visible,
  prefill,
  initialValue,
  onClose,
  onSave,
}: Props) {
  const t = useTranslations();
  const { data: patient } = usePatient();
  const updatePatient = useUpdatePatient();
  const { updateField: updateOnboardingField } = useOnboarding();
  const { height } = useWindowDimensions();
  const dialogMaxHeight = Math.min(820, Math.max(520, height * 0.9));
  const [draft, setDraft] = useState<ApplicantSnapshot>(
    initialValue ?? snapshotFromPrefill(prefill),
  );
  const [saveError, setSaveError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [draftVersion, setDraftVersion] = useState(0);
  const wasVisibleRef = useRef(visible);

  useEffect(() => {
    // Only reset the draft when the dialog opens — not on every re-render.
    // `prefill` is a fresh object identity each render, so including it in the
    // deps would wipe the user's in-progress edits whenever the parent re-ran.
    if (visible && !wasVisibleRef.current) {
      setDraft(initialValue ?? snapshotFromPrefill(prefill));
      setDraftVersion((version) => version + 1);
      setCopied(false);
    }
    wasVisibleRef.current = visible;
  }, [visible, initialValue, prefill]);

  const handleCopy = async () => {
    const text = formatSnapshotForClipboard(draft);
    if (!text) return;
    try {
      await Clipboard.setStringAsync(text);
      setCopied(true);
      haptic("success");
      setTimeout(() => setCopied(false), 2400);
    } catch {
      // Clipboard access can fail silently (e.g. web permission denial);
      // the user can retry without any destructive state change.
    }
  };

  const update = <K extends keyof ApplicantSnapshot>(
    key: K,
    value: ApplicantSnapshot[K],
  ) => setDraft((current) => ({ ...current, [key]: value }));

  const handleSave = async () => {
    setSaveError(false);
    try {
      const patientUpdate = buildPatientUpdate(draft, patient);
      if (Object.keys(patientUpdate).length > 0) {
        await updatePatient.mutateAsync(patientUpdate);
      }
      const nextZipCode = nullableValue(draft.zipCode);
      if (nextZipCode !== nullableValue(prefill.zipCode)) {
        updateOnboardingField("zipCode", nextZipCode);
      }
      onSave(draft);
      onClose();
    } catch {
      setSaveError(true);
    }
  };

  return (
    <Dialog
      containerStyle={{ maxHeight: dialogMaxHeight }}
      size="wide"
      visible={visible}
      onClose={onClose}
      closeButton="minimal"
      layout="centered"
      surfaceStyle={{
        maxHeight: dialogMaxHeight,
        backgroundColor: semanticColors.surface.sheet,
      }}
      testID="financing-applicant-info-dialog"
      contentStyle={{
        paddingTop: 20,
        paddingHorizontal: 20,
        paddingBottom: 18,
        maxHeight: dialogMaxHeight,
      }}
    >
      <View style={{ flex: 1, minHeight: 0 }}>
        <DialogIcon
          source={APPLICANT_DIALOG_ILLUSTRATION}
          width={76}
          height={76}
          style={{ marginBottom: 8 }}
          testID="financing-applicant-dialog-illustration"
        />
        <DialogHeader
          align="center"
          title={t("costs.financing_page.applicant_dialog_heading")}
          subtitle={t("costs.financing_page.applicant_dialog_intro")}
          style={{
            alignItems: "center",
            width: "100%",
            marginBottom: 14,
          }}
          subtitleStyle={{ textAlign: "center", width: "100%" }}
        />

        <ScrollView
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          style={{ flexShrink: 1, marginTop: 14 }}
          contentContainerStyle={{ gap: 12, paddingBottom: 4 }}
        >
          <SnapshotField
            label={t("costs.financing_page.full_name")}
            value={draft.fullName}
            onChangeText={(value) => update("fullName", value)}
            labelIcon={UserRound}
          />
          <DateField
            label={t("costs.financing_page.date_of_birth")}
            value={normalizeDateOfBirth(draft.dateOfBirth) ?? ""}
            onChange={(value) => update("dateOfBirth", value)}
            labelIcon={CalendarDays}
          />
          <SnapshotField
            label={t("costs.financing_page.email_address")}
            value={draft.email}
            onChangeText={(value) => update("email", value)}
            keyboardType="email-address"
            labelIcon={Mail}
          />
          <SnapshotField
            label={t("costs.financing_page.phone_number")}
            value={draft.phone}
            onChangeText={(value) => update("phone", value)}
            keyboardType="phone-pad"
            labelIcon={Phone}
          />

          <AddressField
            key={draftVersion}
            label={t("costs.financing_page.physical_address")}
            labelIcon={MapPin}
            icon={null}
            value={[draft.addressLine1, draft.city, draft.state, draft.zipCode]
              .filter(Boolean)
              .join(", ")}
            onChange={(address: AddressData) =>
              setDraft((current) => ({
                ...current,
                addressLine1: address.address1 ?? "",
                city: address.city ?? "",
                country: address.country ?? "",
                zipCode: address.postalCode ?? "",
              }))
            }
            placeholder={t("costs.financing_page.physical_address_placeholder")}
          />

          <View style={{ flexDirection: "row", gap: 10 }}>
            <View style={{ flex: 1 }}>
              <SnapshotField
                label={t("costs.financing_page.state")}
                value={draft.state}
                onChangeText={(value) => update("state", value)}
                placeholder="CA"
                labelIcon={MapPin}
              />
            </View>
            <View style={{ flex: 1 }}>
              <SnapshotField
                label={t("costs.financing_page.zip_code")}
                value={draft.zipCode}
                onChangeText={(value) => update("zipCode", value)}
                keyboardType="numeric"
                placeholder="94107"
                labelIcon={MapPin}
              />
            </View>
          </View>

          <SnapshotField
            label={t("costs.financing_page.clinic_name")}
            value={draft.clinicName}
            onChangeText={(value) => update("clinicName", value)}
            labelIcon={Building2}
          />
          <SnapshotField
            label={t("costs.financing_page.treatment_type")}
            value={draft.treatmentType}
            onChangeText={(value) => update("treatmentType", value)}
            placeholder="IVF, egg freezing, medication…"
            labelIcon={Stethoscope}
          />
          <SnapshotField
            label={t("costs.financing_page.estimated_treatment_cost")}
            value={draft.estimatedTreatmentCost}
            onChangeText={(value) => update("estimatedTreatmentCost", value)}
            keyboardType="numeric"
            placeholder="$0"
            labelIcon={DollarSign}
          />
          <SnapshotField
            label={t("costs.financing_page.requested_financing_amount")}
            value={draft.requestedFinancingAmount}
            onChangeText={(value) => update("requestedFinancingAmount", value)}
            keyboardType="numeric"
            placeholder="$0"
            labelIcon={DollarSign}
          />
          <SnapshotField
            label={t("costs.financing_page.employment_status")}
            value={draft.employmentStatus}
            onChangeText={(value) => update("employmentStatus", value)}
            placeholder={t(
              "costs.financing_page.employment_status_placeholder",
            )}
            labelIcon={BriefcaseBusiness}
          />
          <SnapshotField
            label={t("costs.financing_page.employer")}
            value={draft.employer}
            onChangeText={(value) => update("employer", value)}
            labelIcon={Building2}
          />
          <SnapshotField
            label={t("costs.financing_page.occupation")}
            value={draft.occupation}
            onChangeText={(value) => update("occupation", value)}
            labelIcon={BriefcaseBusiness}
          />
          <SnapshotField
            label={t("costs.financing_page.annual_gross_income")}
            value={draft.annualGrossIncome}
            onChangeText={(value) => update("annualGrossIncome", value)}
            keyboardType="numeric"
            placeholder="$0"
            labelIcon={BriefcaseBusiness}
          />
          <SnapshotField
            label={t("costs.financing_page.household_annual_income")}
            value={draft.householdAnnualIncome}
            onChangeText={(value) => update("householdAnnualIncome", value)}
            keyboardType="numeric"
            placeholder="$0"
            labelIcon={UsersRound}
          />
          <SnapshotField
            label={t("costs.financing_page.housing_status")}
            value={draft.housingStatus}
            onChangeText={(value) => update("housingStatus", value)}
            placeholder={t("costs.financing_page.housing_status_placeholder")}
            labelIcon={House}
          />
          <SnapshotField
            label={t("costs.financing_page.monthly_housing_cost")}
            value={draft.monthlyHousingCost}
            onChangeText={(value) => update("monthlyHousingCost", value)}
            keyboardType="numeric"
            placeholder="$0"
            labelIcon={House}
          />
          <SnapshotField
            label={t("costs.financing_page.monthly_debt_payments")}
            value={draft.monthlyDebtPayments}
            onChangeText={(value) => update("monthlyDebtPayments", value)}
            keyboardType="numeric"
            placeholder="$0"
            labelIcon={WalletCards}
          />
          <SnapshotField
            label={t("costs.financing_page.co_applicant_full_name")}
            value={draft.coApplicantFullName}
            onChangeText={(value) => update("coApplicantFullName", value)}
            labelIcon={UsersRound}
          />
          <DateField
            label={t("costs.financing_page.co_applicant_date_of_birth")}
            value={normalizeDateOfBirth(draft.coApplicantDateOfBirth) ?? ""}
            onChange={(value) => update("coApplicantDateOfBirth", value)}
            labelIcon={CalendarDays}
          />
          <SnapshotField
            label={t("costs.financing_page.co_applicant_email")}
            value={draft.coApplicantEmail}
            onChangeText={(value) => update("coApplicantEmail", value)}
            keyboardType="email-address"
            labelIcon={Mail}
          />
          <SnapshotField
            label={t("costs.financing_page.co_applicant_phone")}
            value={draft.coApplicantPhone}
            onChangeText={(value) => update("coApplicantPhone", value)}
            keyboardType="phone-pad"
            labelIcon={Phone}
          />
          <SnapshotField
            label={t("costs.financing_page.co_applicant_annual_gross_income")}
            value={draft.coApplicantAnnualGrossIncome}
            onChangeText={(value) =>
              update("coApplicantAnnualGrossIncome", value)
            }
            keyboardType="numeric"
            placeholder="$0"
            labelIcon={DollarSign}
          />
          <SnapshotField
            label={t("costs.financing_page.co_applicant_employment_status")}
            value={draft.coApplicantEmploymentStatus}
            onChangeText={(value) =>
              update("coApplicantEmploymentStatus", value)
            }
            labelIcon={BriefcaseBusiness}
          />
          <SnapshotField
            label={t("costs.financing_page.co_applicant_relationship")}
            value={draft.coApplicantRelationship}
            onChangeText={(value) => update("coApplicantRelationship", value)}
            placeholder={t(
              "costs.financing_page.co_applicant_relationship_placeholder",
            )}
            labelIcon={UsersRound}
          />

          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 17,
              color: EMBER.colors.textFaint,
            }}
          >
            {t("costs.financing_page.application_profile_disclaimer")}
          </Text>
          {saveError ? (
            <ActionBanner
              variant="important"
              message={t("costs.financing_page.save_applicant_info_error")}
            />
          ) : null}
        </ScrollView>

        <Pressable
          onPress={handleCopy}
          accessibilityRole="button"
          accessibilityLabel={t("costs.financing_page.copy_applicant_info")}
          style={({ pressed }) => ({
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            marginTop: 14,
            paddingVertical: 12,
            paddingHorizontal: 16,
            borderRadius: 14,
            backgroundColor: copied
              ? "rgba(34,139,87,0.12)"
              : pressed
                ? "rgba(0,0,0,0.08)"
                : "rgba(0,0,0,0.04)",
            borderWidth: 1,
            borderColor: copied ? "rgba(34,139,87,0.35)" : "rgba(0,0,0,0.12)",
          })}
        >
          {copied ? (
            <Check size={18} color={EMBER.colors.text} strokeWidth={2} />
          ) : (
            <ClipboardCopy
              size={18}
              color={EMBER.colors.text}
              strokeWidth={2}
            />
          )}
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 15,
              fontWeight: "700",
              color: EMBER.colors.text,
            }}
          >
            {copied
              ? t("costs.financing_page.applicant_info_copied")
              : t("costs.financing_page.copy_applicant_info")}
          </Text>
        </Pressable>

        <DialogActions style={{ marginTop: 10 }}>
          <SecondaryButton
            label={t("costs.financing_page.cancel_applicant_info")}
            onPress={onClose}
            flex={0.8}
          />
          <PrimaryButton
            label={t("costs.financing_page.save_applicant_info")}
            onPress={handleSave}
            loading={updatePatient.isPending}
            disabled={updatePatient.isPending}
            flex={1.2}
          />
        </DialogActions>
      </View>
    </Dialog>
  );
}
